const FREE_LIMITS = {
  responsesPerMonth: 100,
  aiPromptsPerMonth: 5,
  forms: 3,
  polls: 3,
  feedback: 1,
  notes: 50,
} as const

const LITE_LIMITS = {
  responsesPerMonth: 1000,
  aiPromptsPerMonth: 50,
  forms: 10,
  polls: 10,
  feedback: 3,
  notes: Infinity,
} as const

const PRO_LIMITS = {
  responsesPerMonth: 5000,
  aiPromptsPerMonth: 200,
  forms: 25,
  polls: 25,
  feedback: 10,
  notes: Infinity,
} as const

export const PLANS = {
  free: {
    id: 'free' as const,
    name: 'Free',
    price: { amount: 0, currency: 'INR', symbol: '₹', period: 'month' },
    tagline: 'Perfect for getting started and personal projects.',
    limits: FREE_LIMITS,
    features: {
      fileUploads: false,
      passwordProtection: false,
      expirationDates: false,
      maxResponses: false,
      hideBranding: false,
      csvExport: false,
      aiSummarization: false,
      pushNotifications: false,
      emailReceipts: false,
      inviteOnly: false,
      conditionalLogic: false,
      analytics: false,
    },
    marketingFeatures: [
      `${FREE_LIMITS.notes} notes`,
      `${FREE_LIMITS.forms} forms, ${FREE_LIMITS.polls} polls, ${FREE_LIMITS.feedback} feedback box`,
      `${FREE_LIMITS.responsesPerMonth} responses / month`,
      `${FREE_LIMITS.aiPromptsPerMonth} AI prompts / month`,
      'Public sharing & real-time sync',
      'Basic AI form generation',
      'Draft saving',
      'In-app notifications',
    ],
  },
  lite: {
    id: 'lite' as const,
    name: 'Lite',
    price: { amount: 99, currency: 'INR', symbol: '₹', period: 'month' },
    tagline: 'Best for creators and growing usage.',
    limits: LITE_LIMITS,
    features: {
      fileUploads: false,
      passwordProtection: true,
      expirationDates: true,
      maxResponses: true,
      hideBranding: false,
      csvExport: true,
      aiSummarization: false,
      pushNotifications: true,
      emailReceipts: true,
      inviteOnly: true,
      conditionalLogic: true,
      analytics: false,
    },
    marketingFeatures: [
      'Unlimited notes',
      `${LITE_LIMITS.forms} forms, ${LITE_LIMITS.polls} polls, ${LITE_LIMITS.feedback} feedback boxes`,
      `${LITE_LIMITS.responsesPerMonth.toLocaleString()} responses / month`,
      `${LITE_LIMITS.aiPromptsPerMonth} AI prompts / month`,
      'CSV export',
      'Conditional logic for forms',
      'Private & invite-only sharing',
      'Short links',
      'Password protection',
      'Expiration dates & max responses',
      'Push notifications',
      'Email receipts',
    ],
  },
  pro: {
    id: 'pro' as const,
    name: 'Pro',
    price: { amount: 299, currency: 'INR', symbol: '₹', period: 'month' },
    tagline: 'For freelancers, creators, and small businesses.',
    limits: PRO_LIMITS,
    features: {
      fileUploads: true,
      passwordProtection: true,
      expirationDates: true,
      maxResponses: true,
      hideBranding: true,
      csvExport: true,
      aiSummarization: true,
      pushNotifications: true,
      emailReceipts: true,
      inviteOnly: true,
      conditionalLogic: true,
      analytics: true,
    },
    marketingFeatures: [
      'Unlimited notes',
      `${PRO_LIMITS.forms} forms, ${PRO_LIMITS.polls} polls, ${PRO_LIMITS.feedback} feedback boxes`,
      `${PRO_LIMITS.responsesPerMonth.toLocaleString()} responses / month`,
      `${PRO_LIMITS.aiPromptsPerMonth} AI prompts / month`,
      'Form analytics & AI insights',
      'AI response summarization',
      'CSV export',
      'Conditional logic for forms',
      'File upload fields (2MB/file, 500MB total)',
      'Private & invite-only sharing',
      'Password protection',
      'Expiration dates & max responses',
      'Push notifications (FCM)',
      'Email receipts for respondents',
      'Short links with custom slugs',
      'Remove Quikee branding',
    ],
  },
}

/**
 * Maps form field types to the minimum plan required to use them.
 * Add entries here when new field types are gated behind a plan.
 */
export const FIELD_PLAN_REQUIREMENTS: Partial<Record<string, 'lite' | 'pro'>> = {
  file: 'pro',
}

/** Backward-compat alias — existing `import { PLAN_LIMITS } from '@/config/plans'` still works */
export const PLAN_LIMITS = {
  free: PLANS.free.limits,
  lite: PLANS.lite.limits,
  pro: PLANS.pro.limits,
} as const
